import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Button, Grid } from '@frontierag/frontier-ui-library';
import type { IPageLayoutScrollSizes } from 'components/PageLayout';
import { usePageLayout } from 'components/PageLayout';
import { useCustomPrompt } from 'hooks/useCustomPrompt';
import LoadingButton from 'shared/LoadingButton/LoadingButton';

import useStyles from './styles';
import type { IWizardForm } from './types';
import { useWizard } from './useWizardForm';

const hasScrollableContent = ({
  scrollHeight = 0,
  offsetHeight = 0,
  scrollTop = 0,
}: IPageLayoutScrollSizes): boolean => scrollHeight - offsetHeight > scrollTop;

export default function WizardForm({
  steps,
  validateOnNext = true,
  setTouchedOnMount = false,
  initialFormValues,
  onSubmit,
  onCancel,
  routePath,
  backLabel,
  cancelLabel,
  nextLabel,
  submitLabel,
  isSaving,
  render,
  promptData = { title: '', message: '', onActionLabel: '', baseExcludedPath: '' },
}: IWizardForm) {
  const { t } = useTranslation();
  const [displayShadow, setDisplayShadow] = useState<boolean>(false);
  const { sizes, scrollSizes } = usePageLayout();
  const actionsRef = useRef<HTMLDivElement>(null);
  const styles = useStyles(displayShadow);

  const { currentStep, form, isFirstStep, isLastStep, handlePrev, handleNext, isNextDisabled } =
    useWizard(steps, validateOnNext, initialFormValues, onSubmit, routePath, setTouchedOnMount);

  const { component: StepComponent, containerGridProps = {}, actionsGridProps = {} } = currentStep;

  const handleDisplayShadow = useCallback(() => {
    if (scrollSizes && displayShadow !== hasScrollableContent(scrollSizes)) {
      setDisplayShadow(hasScrollableContent(scrollSizes));
    }
  }, [displayShadow, scrollSizes]);

  useEffect(() => {
    handleDisplayShadow();
  }, [handleDisplayShadow]);
  useEffect(() => {
    if (sizes?.height) handleDisplayShadow();
  }, [handleDisplayShadow, sizes]);

  const actionsHeight = actionsRef?.current ? actionsRef?.current.scrollHeight : 0;

  useCustomPrompt(
    promptData,
    form.formState.isDirty && !form.formState.isSubmitting && form.formState.submitCount === 0,
  );

  return (
    <FormProvider {...form}>
      <Grid container justifyContent="center" direction="row" data-testid="wizard-grid">
        <Grid width="100%" item {...containerGridProps}>
          {render ? render(form.getValues(), StepComponent) : StepComponent}
          <Box height={actionsHeight} />
          <Box sx={styles.fixedActions} ref={actionsRef} data-testid="wizard-footer-actions">
            <Grid justifyContent="center" spacing={2} container {...actionsGridProps}>
              <Grid item xs={6} justifyContent="flex-end">
                {isFirstStep ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={onCancel}
                    data-testid="btn-cancel"
                  >
                    {cancelLabel || t('buttons.cancel')}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={handlePrev}
                    disabled={isSaving}
                    data-testid="btn-prev"
                  >
                    {backLabel || t('buttons.back')}
                  </Button>
                )}
              </Grid>
              <Grid item xs={6}>
                {!isLastStep ? (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={isNextDisabled}
                    fullWidth
                    onClick={handleNext}
                    data-testid="btn-next"
                  >
                    {nextLabel || t('buttons.next')}
                  </Button>
                ) : (
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    disabled={isNextDisabled}
                    fullWidth
                    onClick={handleNext}
                    loading={Boolean(isSaving)}
                    data-testid="btn-submit"
                  >
                    {submitLabel || t('buttons.submit')}
                  </LoadingButton>
                )}
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
